import { type BaseSyntheticEvent, useEffect, useState, useRef } from 'react'

export function smoothScroll(event: BaseSyntheticEvent, offset?: number) {
  const isSmoothScrollSupported = 'scrollBehavior' in document.documentElement.style
  if (isSmoothScrollSupported) {
    event.preventDefault()
    let { target } = event
    while (target && !target.hash) target = target.parentElement

    let hash
    hash = decodeURIComponent(target?.hash)

    if (hash === 'undefined') {
      hash = `#${event.target.getAttribute('id')}`
    }

    const element = document.getElementById(hash?.substring(1).replace(/\/$/, ''))
    if (element) {
      if (!offset) {
        element.scrollIntoView({ behavior: 'smooth' })
      } else {
        const rect = element.getBoundingClientRect()
        const elementPosition = rect.top + window.scrollY
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth',
        })
      }

      window.history.replaceState(null, null, hash)
    }
  }
}

export function isScrolledToTop() {
    return window.scrollY === 0 || document.documentElement.scrollTop === 0;
}

export function useScrollSpy({ id, offset = 0 }: {id:  string, offset?: number}) {
  useEffect(() => {
    const scrollables = document.querySelectorAll('.scrollspy')
    const navLinks = document.querySelectorAll(`#${id} a`)

    if (scrollables.length === 0 || navLinks.length === 0) return

    // Function to update active state
    const updateActiveState = (targetId: string | null) => {
      // Remove active class from all nav links
      navLinks.forEach((link) => link.parentElement?.classList.remove('scrollactive'))

      if (targetId) {
        // Add active class to matching link
        navLinks.forEach((link) => {
          if (link.getAttribute('href') === `#${targetId}`) {
            // add scrollactive to its parent
            link.parentElement?.classList.add('scrollactive')
          }
        })
      }
    }

    // Get section positions and find the active one based on scroll position
    const findActiveSection = () => {
      const scrollTop = window.scrollY
      let activeSection: string | null = null

      // Convert to array and get section data
      const sections = Array.from(scrollables).map(section => ({
        id: section.id,
        element: section as HTMLElement,
        top: (section as HTMLElement).offsetTop,
        bottom: (section as HTMLElement).offsetTop + (section as HTMLElement).offsetHeight
      }))

      // Sort by position
      sections.sort((a, b) => a.top - b.top)

      // Find which section we're currently in
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i]
        const sectionStart = section.top - offset
        const sectionEnd = section.bottom - offset

        // If we're past this section's start
        if (scrollTop >= sectionStart) {
          activeSection = section.id
        }

        // If we haven't reached the first section yet, clear active state
        if (i === 0 && scrollTop < sectionStart) {
          activeSection = null
        }
      }

      return activeSection
    }

    // Handle scroll events
    const handleScroll = () => {
      const activeSection = findActiveSection()
      updateActiveState(activeSection)
    }

    // Set initial state
    handleScroll()

    // Add scroll listener with throttling for performance
    let ticking = false
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledHandleScroll, { passive: true })

    // Cleanup function
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
    }
  }, [id])
}

export function downloadBlob(content, type, filename) {
  const blob = new Blob([content], { type })
  const link = document.createElement('a')

  link.download = filename
  link.href = window.URL.createObjectURL(blob)
  link.dataset.downloadurl = [type, link.download, link.href].join(':')

  link.dispatchEvent(
    new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
    })
  )
  link.remove()
}

export function selectElementText(elId: string) {
  const el = document.getElementById(elId)
  const sel = window.getSelection()
  if (sel) sel.removeAllRanges()
  if (el) {
    const range = window.document.createRange()
    if (range) {
      range.selectNodeContents(el)
      if (sel) sel.addRange(range)
    }
  }
}

// eslint-disable-next-line import/prefer-default-export
export function useHasScrolled(distance = '10px') {
  const topRef = useRef(null)
  const [hasScrolled, setHasScrolled] = useState(false)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        setHasScrolled(!entry.isIntersecting)
      },
      { rootMargin: distance }
    )

    observer.observe(topRef.current)
  }, [])
  return [topRef, hasScrolled]
}
